import { type AskPunttReview } from "@mg/schemas/src/christo/askPunttReview";
import { CaretDown } from "@phosphor-icons/react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export type ReviewVersionOption = {
  reviewId: string;
  title: string;
};

export function VersionPicker({
  review,
  options,
  onSelect,
}: {
  review: AskPunttReview;
  options: readonly ReviewVersionOption[];
  onSelect: (reviewId: string) => void;
}) {
  const { t } = useTranslation("ui");
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const disabled = options.length <= 1;

  // why: this menu is a hand-rolled popup, not a Radix one, so nothing ever
  // dismissed it. Opening it and then reading on -- clicking the artwork, a
  // finding, the close control -- left it hanging over the header at z-20
  // until the trigger happened to be pressed a second time, and Escape did
  // nothing at all. Capture phase because the artwork pane takes pointerdown
  // for pan and pin placement; a target that stops propagation must not be
  // able to keep the menu open.
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setOpen(false);
      // A reader who tabbed into the list would otherwise be dropped on
      // <body> when the option they were on unmounts.
      triggerRef.current?.focus();
    };
    const handlePointerDown = (event: PointerEvent) => {
      if (containerRef.current?.contains(event.target as Node)) return;
      setOpen(false);
    };
    document.addEventListener("keydown", handleKeyDown, true);
    document.addEventListener("pointerdown", handlePointerDown, true);
    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
      document.removeEventListener("pointerdown", handlePointerDown, true);
    };
  }, [open]);

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        ref={triggerRef}
        disabled={disabled}
        aria-disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t("askV2.review.versionPickerLabel")}
        data-testid="ask-v2-review-version-picker"
        className="flex max-w-[220px] items-center gap-1 rounded-md border border-puntt-neutral-gray-5 bg-base-white px-2.5 py-1.5 text-[13px] font-medium text-puntt-neutral-gray-12 disabled:cursor-default disabled:opacity-70"
        onClick={() => !disabled && setOpen((current) => !current)}
      >
        <span className="truncate">{review.title}</span>
        <CaretDown size={14} aria-hidden="true" />
      </button>
      {open && options.length > 1 ? (
        <ul
          className="absolute left-0 top-full z-20 mt-1 min-w-[220px] rounded-md border border-puntt-neutral-gray-5 bg-base-white py-1 shadow-lg"
          role="listbox"
          // why: the popup that opens is a second thing to name, not the same
          // thing as the trigger -- unnamed, a reader who opens it is told only
          // "list box" and has to infer what it is picking between.
          aria-label={t("askV2.review.versionPickerLabel")}
        >
          {options.map((option) => (
            // why: a `listbox` may own only `option` (or `group`). Left as a
            // plain `<li>` each wrapper is a `listitem`, so this menu reached a
            // reader as a two-item list with a button inside each -- no "option
            // 1 of 2", and the `aria-selected` that marks the version currently
            // open sat on an element the listbox does not own. That is the same
            // axe aria-required-children failure ReviewPaneTabs above was fixed
            // for. `role="none"` drops the wrapper out of the accessibility
            // tree so the listbox owns the option buttons directly, and leaves
            // focus, clicks and the testid on the button where they already are.
            <li key={option.reviewId} role="none">
              <button
                type="button"
                role="option"
                aria-selected={option.reviewId === review.reviewId}
                className="block w-full px-3 py-2 text-left text-[13px] hover:bg-puntt-neutral-gray-2"
                data-testid={`ask-v2-review-version-option-${option.reviewId}`}
                onClick={() => {
                  setOpen(false);
                  onSelect(option.reviewId);
                }}
              >
                {option.title}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
